import { useEffect, useState } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import apiReq from 'core/api';
import shellMuiTheme from 'components/AppShell/muiTheme';

const BITRIX_SUCCESS_PATH = '/integrations/bitrix/success';

/**
 * Проверяет, что бэк вернул валидный URL Bitrix OAuth.
 * Защита от случая, когда сервер не сконфигурирован и отдаёт URL
 * с пустыми client_id/redirect_uri/scope или с хостом фронта.
 */
const validateBitrixAuthUrl = (raw: string): string | null => {
  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return 'Сервер вернул некорректную ссылку авторизации';
  }

  // Хост должен отличаться от нашего фронта (Bitrix OAuth живёт на oauth.bitrix.info
  // или на домене конкретного портала).
  if (parsed.host === window.location.host) {
    return 'Битрикс не настроен на сервере: получен URL на наш домен';
  }

  const required = ['client_id', 'redirect_uri', 'scope'];
  const missing = required.filter((k) => !parsed.searchParams.get(k));
  if (missing.length > 0) {
    return `Битрикс не настроен на сервере: пустые параметры (${missing.join(', ')})`;
  }

  return null;
};

export default function BitrixLoadingPage() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const returnUrl = `${window.location.origin}${BITRIX_SUCCESS_PATH}`;

    apiReq<{ url: string }>('bitrix/oauth/login', 'POST', { return_url: returnUrl })
      .then((res) => {
        if (!res?.url) {
          setError('Не удалось получить ссылку авторизации');
          return;
        }
        const validationError = validateBitrixAuthUrl(res.url);
        if (validationError) {
          setError(validationError);
          return;
        }
        window.location.href = res.url;
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка при подключении к Битрикс');
      });
  }, []);

  return (
    <ThemeProvider theme={shellMuiTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          gap: 3,
          px: 3,
          fontFamily:
            "'Inter', 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            maxWidth: 360,
          }}
        >
          {/* Логотип / иконка Битрикс */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3,
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (t) => `0 8px 24px ${t.palette.primary.main}40`,
            }}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
              B
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Подключение к Битрикс24
            </Typography>
            {!error && (
              <Typography variant="body2" color="text.secondary">
                Выполняем переход на страницу авторизации...
              </Typography>
            )}
          </Box>

          {error ? (
            <Box
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 2,
                bgcolor: 'error.main',
                color: 'error.contrastText',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">{error}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                Закройте это окно и попробуйте снова
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                sx={{
                  height: 3,
                  borderRadius: 2,
                  bgcolor: 'divider',
                  '& .MuiLinearProgress-bar': { borderRadius: 2 },
                }}
              />
            </Box>
          )}
        </Box>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ position: 'absolute', bottom: 24 }}
        >
          Требуется авторизация в Битрикс24
        </Typography>
      </Box>
    </ThemeProvider>
  );
}
